import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarFrame } from '../../shared/components/AvatarFrame.js';
import { VoterCluster } from '../../shared/components/VoterCluster.js';
import { ClueSlip } from '../../shared/components/ClueSlip.js';
import { ANOTHER_ROUND, type DiscussionPlayer, type DiscussionResolution } from '../types.js';
import { SentinelTile } from './SentinelTile.js';
import '../../shared/components/CastGrid.css';
import './CastGrid.css';

interface CastGridProps {
  /** Alive players, sorted by turnOrder. */
  players: DiscussionPlayer[];
  /** Voters grouped by target id. Keyed by player id OR `ANOTHER_ROUND` (the
   *  sentinel). The voters are the seat data the cluster glyphs render from. */
  votersByTarget: ReadonlyMap<string, DiscussionPlayer[]>;
  /** The local viewer's current target (player id | `ANOTHER_ROUND`), if any. */
  myVoteTarget?: string;
  /** `voting` → live. Otherwise the locked outcome: a leading cluster firms to
   *  Oxblood, and on an elimination the target's name-plate flips Oxblood. */
  resolution: DiscussionResolution;
  /** targetId is a player id or `ANOTHER_ROUND`. Re-firing the current target
   *  clears the vote (the parent decides; the grid just reports the tap). */
  onVote?: (targetId: string) => void;
  /** Cold-arrival suppression, threaded to VoterCluster + AvatarFrame + ClueSlip. */
  justArrived?: boolean;
  /** True when the local viewer is dead (eliminated, still watching). The
   *  grid renders the reveal arc's static treatment — plain cells, no radio
   *  semantics, no vote dispatch — while the public clusters stay live, so
   *  the dead viewer watches the tally without being handed a ballot the
   *  server would reject. Default `false`. */
  spectating?: boolean;
}

/**
 * Discussion cast grid. A radiogroup of every alive player PLUS the trailing
 * "Another Round" sentinel: tapping a cell casts / re-casts a vote to eliminate
 * that player (or to call another round); tapping the current target again
 * clears it. Each player cell carries the shared atoms — an AvatarFrame, the
 * top-edge VoterCluster (public tally; this is where the "+N" overflow earns
 * its keep), and the persisted ClueSlip (the evidence under vote).
 *
 * Distinct from ghost-meeting's grid: all alive players (not Ghosts-only), the
 * sentinel, no fellow-Ghost marks, and the "your vote" state is NEUTRAL ink —
 * a deep frame + inner rule — not the Spectral border (that only read because
 * the ghost-meeting voter was a Ghost; here the voter is the villager).
 */
export function CastGrid({
  players,
  votersByTarget,
  myVoteTarget,
  resolution,
  onVote,
  justArrived = false,
  spectating = false,
}: CastGridProps): JSX.Element {
  const { t } = useTranslation();

  const lockedTarget =
    resolution.kind === 'eliminate'
      ? resolution.targetId
      : resolution.kind === 'anotherRound'
        ? ANOTHER_ROUND
        : null;
  const eliminatedTarget = resolution.kind === 'eliminate' ? resolution.targetId : null;
  // On a split the room "could not decide": clusters clear (the scattered tally
  // is no longer the story; the verso carries the line instead).
  const showClusters = resolution.kind !== 'split';

  const renderCell = (cellId: string, content: JSX.Element, voteLabel: string): JSX.Element => {
    const voters = showClusters ? (votersByTarget.get(cellId) ?? []) : [];
    if (spectating) {
      /* Dead-viewer cell: the RevealCast static treatment (data-static
         opts out of the hover/cursor affordance) on a plain <span> — no
         role="radio", no aria-checked, no vote label, no testid. The
         cluster and the lock/eliminated marks stay: the tally is public
         and the dead viewer is here to watch it. */
      return (
        <div key={cellId}>
          <span
            className="susdle-cast-cell"
            data-static="yes"
            data-locked={lockedTarget === cellId ? 'yes' : 'no'}
            data-eliminated={eliminatedTarget === cellId ? 'yes' : 'no'}
            data-float-anchor=""
          >
            <VoterCluster voters={voters} justArrived={justArrived} />
            {content}
          </span>
        </div>
      );
    }
    const voted = myVoteTarget === cellId;
    return (
      <div key={cellId}>
        <button
          type="button"
          className="susdle-cast-cell"
          role="radio"
          aria-checked={voted}
          aria-label={voteLabel}
          data-myvote={voted ? 'yes' : 'no'}
          data-locked={lockedTarget === cellId ? 'yes' : 'no'}
          data-eliminated={eliminatedTarget === cellId ? 'yes' : 'no'}
          data-float-anchor=""
          data-testid={`ballot-vote-${cellId}`}
          onClick={() => onVote?.(cellId)}
        >
          <VoterCluster voters={voters} justArrived={justArrived} />
          {content}
        </button>
      </div>
    );
  };

  return (
    <div
      className="susdle-cast-grid"
      role={spectating ? 'group' : 'radiogroup'}
      aria-label={t('discussion.cast.ariaLabel')}
    >
      {players.map((player) =>
        renderCell(
          player.id,
          <>
            <AvatarFrame
              player={player}
              isHost={false}
              away={!player.connected}
              suppressEntry={justArrived}
            />
            <ClueSlip
              clue={player.clue}
              ownerName={player.name}
              suppressInk={justArrived}
              testId={`clue-slip-${player.id}`}
            />
          </>,
          myVoteTarget === player.id
            ? t('discussion.cast.clearVote', { name: player.name })
            : t('discussion.cast.voteFor', { name: player.name }),
        ),
      )}
      {renderCell(
        ANOTHER_ROUND,
        <SentinelTile label={t('discussion.cast.anotherRound')} />,
        myVoteTarget === ANOTHER_ROUND
          ? t('discussion.cast.clearAnotherRound')
          : t('discussion.cast.voteAnotherRound'),
      )}
    </div>
  );
}
